import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpEventType } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { finalize, forkJoin } from 'rxjs';
import { MaterialModule } from '../material.module';
import { ConfirmDialogComponent } from '../shared/confirm-dialog/confirm-dialog.component';
import { CalibrationRecordEditDialogComponent } from './calibration-record-edit-dialog.component';
import { InstrumentFormDialogComponent } from './instrument-form-dialog.component';
import { InstrumentStatusDialogComponent } from './instrument-status-dialog.component';
import {
  BlenderBladeLogEntry,
  CalibrationRecord,
  CalibrationRecordType,
  CalibrationRecordValue,
  CalibrationTemplateField,
  CalibrationTemplateItem,
  CreateCalibrationRecordRequest,
  CalibrationValueRequest,
  InstrumentCalibrationItem,
  InstrumentLifecycleEvent,
  LabEquipmentAttachment,
  LabEquipmentInstrument,
  LabEquipmentLocation,
  MaintenanceRecord,
  OutOfSpecificationResponse,
} from './lab-equipment.models';
import { LabEquipmentService } from './lab-equipment.service';

interface CalibrationFieldGroup {
  key: string;
  fields: CalibrationTemplateField[];
  isPair: boolean;
  startsNewRow: boolean;
}

@Component({
  selector: 'app-instrument-details',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MaterialModule, RouterModule],
  templateUrl: './instrument-details.component.html',
  styleUrl: './instrument-details.component.scss',
})
export class InstrumentDetailsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  readonly service = inject(LabEquipmentService);
  private readonly dialog = inject(MatDialog);
  instrument: LabEquipmentInstrument | null = null;
  locations: LabEquipmentLocation[] = [];
  templateItems: CalibrationTemplateItem[] = [];
  assignments: InstrumentCalibrationItem[] = [];
  calibrationRecords: CalibrationRecord[] = [];
  maintenanceRecords: MaintenanceRecord[] = [];
  attachments: LabEquipmentAttachment[] = [];
  lifecycleEvents: InstrumentLifecycleEvent[] = [];
  bladeLogEntries: BlenderBladeLogEntry[] = [];
  completionRemovedDates: Record<number, string> = {};
  completionWeights: Record<number, number | null> = {};
  isSavingBladeEntry = false;
  completingBladeEntryId: number | null = null;
  isLoading = true;
  errorMessage = '';
  selectedTemplateItemId: number | null = null;
  initialDueDate = this.today();
  activationDueDates: Record<number, string> = {};
  selectedAssignmentId: number | null = null;
  nextDueOverrideDate = '';
  nextDueOverrideReason = '';
  isSavingOverride = false;
  calibrationFields: CalibrationTemplateField[] = [];
  calibrationValues = new FormGroup<Record<string, FormControl<string | number | boolean | null>>>({});
  historyYear = new Date().getFullYear();
  readonly weeks = Array.from({ length: 53 }, (_, index) => index + 1);
  historyWeek: number | null = null;
  historyMonth: number | null = null;
  historyQuarter: number | null = null;
  addendumText: Record<number, string> = {};
  selectedFile: File | null = null;
  isDragOver = false;
  uploadMaintenanceRecordId: number | null = null;
  documentDate = '';
  isSavingCalibration = false;
  isSavingMaintenance = false;
  isDeletingInstrument = false;
  isUploading = false;
  uploadProgress: number | null = null;

  readonly calibrationForm = new FormGroup({
    recordType: new FormControl<CalibrationRecordType>('Scheduled', { nonNullable: true }),
    periodYear: new FormControl(new Date().getFullYear(), { nonNullable: true, validators: [Validators.required, Validators.min(2000)] }),
    periodNumber: new FormControl(1, { nonNullable: true, validators: [Validators.required, Validators.min(1)] }),
    calibrationDate: new FormControl(this.today(), { nonNullable: true, validators: Validators.required }),
    notes: new FormControl('', { nonNullable: true, validators: Validators.maxLength(4000) }),
  });

  readonly bladeInstallationForm = new FormGroup({
    blenderNumber: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(50)] }),
    installedDate: new FormControl(this.today(), { nonNullable: true, validators: Validators.required }),
    weightBefore: new FormControl<number | null>(null, { validators: [Validators.required, Validators.min(0.000001)] }),
  });

  readonly maintenanceForm = new FormGroup({
    type: new FormControl<'Maintenance' | 'Repair'>('Maintenance', { nonNullable: true, validators: Validators.required }),
    performedDate: new FormControl(this.today(), { nonNullable: true, validators: Validators.required }),
    notes: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(4000)] }),
  });

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!Number.isInteger(id) || id <= 0) {
      this.errorMessage = 'The instrument ID is invalid.';
      this.isLoading = false;
      return;
    }
    this.loadAll(id);
  }

  get isReadOnly(): boolean { return this.instrument?.isDeleted ?? true; }
  get isBlenderBladeWeightLog(): boolean { return this.instrument?.instrumentTypeId === 28; }

  get visibleAssignments(): InstrumentCalibrationItem[] {
    return this.assignments;
  }

  get selectedAssignment(): InstrumentCalibrationItem | undefined {
    return this.visibleAssignments.find(item => item.id === this.selectedAssignmentId);
  }

  get calibrationFieldGroups(): CalibrationFieldGroup[] {
    const groups: CalibrationFieldGroup[] = [];
    const pairedGroups = new Map<string, CalibrationFieldGroup>();

    for (const field of this.calibrationFields) {
      if (!field.pairKey) {
        groups.push({ key: `field-${field.id}`, fields: [field], isPair: false, startsNewRow: false });
        continue;
      }

      let group = pairedGroups.get(field.pairKey);
      if (!group) {
        group = { key: `pair-${field.pairKey}`, fields: [], isPair: true, startsNewRow: false };
        pairedGroups.set(field.pairKey, group);
        groups.push(group);
      }
      group.fields.push(field);
    }

    for (let index = 0; index < groups.length - 1; index++) {
      const current = groups[index];
      const previous = groups[index - 1];
      const next = groups[index + 1];
      const isSingleTextField = current.fields.length === 1 && current.fields[0].valueType === 'Text';
      const startsTextRun = !previous || previous.fields.some(field => field.valueType !== 'Text');
      const followedByNumericFields = next.fields.every(field => field.valueType === 'Numeric');
      next.startsNewRow = isSingleTextField && startsTextRun && followedByNumericFields;
    }

    return groups;
  }

  get selectedItem(): CalibrationTemplateItem | undefined {
    const templateItemId = this.selectedAssignment?.calibrationTemplateItemId;
    return this.templateItems.find(item => item.id === templateItemId);
  }

  get availableTemplateItems(): CalibrationTemplateItem[] {
    const assignedIds = new Set(this.assignments.map(item => item.calibrationTemplateItemId));
    return this.templateItems.filter(item => item.isActive && !assignedIds.has(item.id));
  }

  get availableTemplateItemGroups(): { frequency: string; items: CalibrationTemplateItem[] }[] {
    const frequencyOrder = ['Weekly', 'Monthly', 'Quarterly', 'Annual'];
    return frequencyOrder
      .map(frequency => ({
        frequency,
        items: this.availableTemplateItems
          .filter(item => item.frequency === frequency)
          .sort((left, right) => left.name.localeCompare(right.name)),
      }))
      .filter(group => group.items.length > 0);
  }

  get periodLabel(): string {
    return this.selectedAssignment?.frequency === 'Weekly'
      ? 'Week'
      : this.selectedAssignment?.frequency === 'Monthly'
      ? 'Month'
      : this.selectedAssignment?.frequency === 'Quarterly' ? 'Quarter' : 'Period';
  }

  formatDueStatus(status: string | null | undefined): string {
    if (!status) return 'Not scheduled';
    const spaced = status.replace(/([a-z])([A-Z])/g, '$1 $2');
    return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
  }

  calibrationValue(value: CalibrationRecordValue): string {
    const measured = value.numericValue ?? value.booleanValue ?? value.textValue;
    if (measured === null) return 'Not recorded';
    const displayValue = typeof measured === 'boolean' ? (measured ? 'Yes' : 'No') : measured;
    return `${displayValue}${value.unit ? ` ${value.unit}` : ''}`;
  }

  targetValue(value: CalibrationRecordValue): string {
    const unit = value.unit ? ` ${value.unit}` : '';
    return value.targetValue !== null ? `${value.targetValue}${unit}` : 'Not set';
  }

  rangeValue(value: CalibrationRecordValue): string {
    const unit = value.unit ? ` ${value.unit}` : '';
    if (value.minimumValue !== null && value.maximumValue !== null) {
      return `${value.minimumValue}-${value.maximumValue}${unit}`;
    }
    if (value.minimumValue !== null) return `At least ${value.minimumValue}${unit}`;
    if (value.maximumValue !== null) return `At most ${value.maximumValue}${unit}`;
    return value.acceptanceCriteria || 'Not set';
  }

  calibrationRecordTitle(record: CalibrationRecord): string {
    const assignment = this.assignments.find(item => item.id === record.instrumentCalibrationItemId);
    return assignment ? `${record.frequency} ${assignment.name}` : `${record.frequency} calibration`;
  }

  openDatePicker(input: HTMLInputElement): void {
    input.showPicker?.();
  }

  addendumActor(createdBy: string): string {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(createdBy)
      ? 'Unknown user'
      : createdBy;
  }

  editInstrument(): void {
    if (!this.instrument || this.isReadOnly) return;
    const instrumentId = this.instrument.id;
    this.dialog.open(InstrumentFormDialogComponent, {
      width: '820px',
      maxWidth: '95vw',
      data: { instrument: this.instrument, locations: this.locations },
    }).afterClosed().subscribe(saved => {
      if (saved) this.loadAll(instrumentId);
    });
  }

  moveToRemoved(): void {
    if (!this.instrument || this.isReadOnly) return;
    const instrumentId = this.instrument.id;
    this.dialog.open(InstrumentStatusDialogComponent, {
      width: '520px',
      maxWidth: '95vw',
      data: { instrument: this.instrument, mode: 'remove' },
    }).afterClosed().subscribe(changed => {
      if (changed) this.loadAll(instrumentId);
    });
  }

  permanentlyDeleteInstrument(): void {
    if (!this.instrument?.isDeleted || this.isDeletingInstrument) return;
    const instrument = this.instrument;
    this.dialog.open(ConfirmDialogComponent, {
      width: '520px',
      maxWidth: 'calc(100vw - 32px)',
      data: {
        title: 'Permanently Delete Instrument',
        message: `This permanently deletes ${instrument.name} and its calibration, maintenance/repair, lifecycle history, and attached files from the application. This cannot be undone.`,
        confirmLabel: 'Delete Permanently',
        cancelLabel: 'Cancel',
        variant: 'warning',
      },
    }).afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.isDeletingInstrument = true;
      this.service.permanentlyDeleteInstrument(instrument.id)
        .pipe(finalize(() => this.isDeletingInstrument = false))
        .subscribe({
          next: () => void this.router.navigate(['/lab-equipment']),
          error: error => this.showError(error, 'The instrument could not be permanently deleted.'),
        });
    });
  }

  assignItem(): void {
    if (!this.instrument || !this.selectedTemplateItemId || this.isReadOnly) return;
    this.service.assignCalibrationItem(this.instrument.id, this.selectedTemplateItemId, this.initialDueDate).subscribe({
      next: () => {
        this.selectedTemplateItemId = null;
        this.loadAssignments();
      },
      error: error => this.showError(error, 'The calibration item could not be assigned.'),
    });
  }

  requiresActivationDueDate(assignment: InstrumentCalibrationItem): boolean {
    return !assignment.isActive && !assignment.initialDueDate && !assignment.lastCompletedDate;
  }

  toggleAssignment(assignment: InstrumentCalibrationItem): void {
    if (!this.instrument || this.isReadOnly) return;
    const isActive = !assignment.isActive;
    const initialDueDate = isActive && this.requiresActivationDueDate(assignment)
      ? this.activationDueDates[assignment.id]
      : null;
    if (isActive && this.requiresActivationDueDate(assignment) && !initialDueDate) return;

    this.service.updateCalibrationAssignment(this.instrument.id, assignment.id, isActive, initialDueDate).subscribe({
      next: () => {
        delete this.activationDueDates[assignment.id];
        this.loadAssignments();
      },
      error: error => this.showError(error, 'The calibration item could not be updated.'),
    });
  }

  saveNextDueOverride(clear = false): void {
    if (!this.instrument || !this.selectedAssignment || this.isReadOnly || this.isSavingOverride) return;
    const reason = this.nextDueOverrideReason.trim();
    if (!reason || (!clear && !this.nextDueOverrideDate)) return;
    if (clear && !this.selectedAssignment.nextDueDateOverride) return;

    this.isSavingOverride = true;
    this.service.updateNextDueOverride(this.instrument.id, this.selectedAssignment, clear ? null : this.nextDueOverrideDate, reason)
      .pipe(finalize(() => this.isSavingOverride = false))
      .subscribe({
        next: () => {
          this.nextDueOverrideReason = '';
          this.loadAssignments();
        },
        error: error => this.showError(error, 'The Next Due date could not be updated.'),
      });
  }

  deleteAssignment(assignment: InstrumentCalibrationItem): void {
    if (!this.instrument || this.isReadOnly || assignment.hasCalibrationRecords !== false || assignment.hasOverrideHistory) return;
    const instrumentId = this.instrument.id;
    this.dialog.open(ConfirmDialogComponent, {
      width: '520px',
      maxWidth: 'calc(100vw - 32px)',
      data: {
        title: 'Delete Calibration Item',
        message: `Delete ${assignment.name} from this instrument? This action cannot be undone.`,
        confirmLabel: 'Delete',
        cancelLabel: 'Cancel',
        variant: 'warning',
      },
    }).afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.service.deleteCalibrationAssignment(instrumentId, assignment.id).subscribe({
        next: () => {
          if (this.selectedAssignmentId === assignment.id) this.selectedAssignmentId = null;
          this.loadAssignments();
        },
        error: error => this.showError(error, 'The calibration item could not be deleted.'),
      });
    });
  }

  selectAssignment(id: number): void {
    this.selectedAssignmentId = id;
    this.nextDueOverrideDate = this.selectedAssignment?.nextDueDateOverride ?? '';
    this.nextDueOverrideReason = '';
    const item = this.selectedAssignment?.isActive ? this.selectedItem : undefined;
    this.calibrationFields = [...(item?.fields ?? [])].sort((a, b) => a.displayOrder - b.displayOrder);
    const controls: Record<string, FormControl<string | number | boolean | null>> = {};
    for (const field of this.calibrationFields) {
      const initialValue = field.valueType === 'Boolean' ? null : '';
      controls[this.fieldKey(field.id)] = new FormControl(initialValue, field.isRequired ? Validators.required : []);
    }
    this.calibrationValues = new FormGroup(controls);
    const periodControl = this.calibrationForm.controls.periodNumber;
    const maximumPeriod = this.selectedAssignment?.frequency === 'Weekly' ? 53
      : this.selectedAssignment?.frequency === 'Monthly' ? 12
      : this.selectedAssignment?.frequency === 'Quarterly' ? 4 : 1;
    periodControl.setValidators([Validators.required, Validators.min(1), Validators.max(maximumPeriod)]);
    periodControl.setValue(1);
    periodControl.updateValueAndValidity();
  }

  get hasCalibrationPairs(): boolean {
    return this.calibrationFields.some(field => field.role === 'Calibrated' && Boolean(field.pairKey));
  }

  copyAsFoundToCalibrated(onComplete?: () => void, requestConfirmation = false): void {
    const pairs = this.calibrationFields
      .filter(target => target.role === 'Calibrated' && target.pairKey && !this.hasValue(target))
      .map(target => ({
        target,
        source: this.calibrationFields.find(source => source.role === 'AsFound' && source.pairKey === target.pairKey),
      }))
      .filter(pair => pair.source && this.hasValue(pair.source)) as { target: CalibrationTemplateField; source: CalibrationTemplateField }[];

    const copy = () => {
      const outsideRange: typeof pairs = [];
      for (const pair of pairs) {
        const sourceValue = this.calibrationValues.controls[this.fieldKey(pair.source.id)].value;
        const numericValue = Number(sourceValue);
        const hasRange = pair.source.minimumValue !== null || pair.source.maximumValue !== null;
        if (hasRange && Number.isNaN(numericValue)) continue;

        const isBelowMinimum = pair.source.minimumValue !== null && numericValue < pair.source.minimumValue;
        const isAboveMaximum = pair.source.maximumValue !== null && numericValue > pair.source.maximumValue;
        if (isBelowMinimum || isAboveMaximum) outsideRange.push(pair);
        else this.calibrationValues.controls[this.fieldKey(pair.target.id)].setValue(sourceValue);
      }

      if (outsideRange.length === 0) {
        onComplete?.();
        return;
      }
      this.dialog.open(ConfirmDialogComponent, {
        width: '520px',
        maxWidth: 'calc(100vw - 32px)',
        data: {
          title: 'As Found Values Outside Range',
          message: 'The readings below are outside their configured ranges. Copy them to the empty Calibrated fields anyway?',
          confirmLabel: 'Copy Anyway',
          cancelLabel: 'Leave Blank',
          variant: 'warning',
          details: outsideRange.map(pair => ({ label: pair.source.label, value: String(this.calibrationValues.controls[this.fieldKey(pair.source.id)].value) })),
        },
      }).afterClosed().subscribe(confirmed => {
        if (!confirmed) return;
        for (const pair of outsideRange) {
          this.calibrationValues.controls[this.fieldKey(pair.target.id)]
            .setValue(this.calibrationValues.controls[this.fieldKey(pair.source.id)].value);
        }
        onComplete?.();
      });
    };

    if (!requestConfirmation) {
      copy();
      return;
    }
    this.dialog.open(ConfirmDialogComponent, {
      width: '460px',
      maxWidth: 'calc(100vw - 32px)',
      data: {
        title: 'Complete Calibrated Values',
        message: 'Copy available As Found readings into the empty Calibrated fields before saving?',
        confirmLabel: 'Copy and Continue',
        cancelLabel: 'Review Values',
      },
    }).afterClosed().subscribe(confirmed => {
      if (confirmed) copy();
    });
  }

  saveCalibration(confirmOutOfSpecification = false): void {
    if (!this.instrument || !this.selectedAssignment?.isActive || !this.selectedItem || this.isReadOnly) return;
    const hasCopyCandidates = this.calibrationFields.some(target => target.role === 'Calibrated' && target.pairKey
      && !this.hasValue(target) && this.calibrationFields.some(source => source.role === 'AsFound'
        && source.pairKey === target.pairKey && this.hasValue(source)));
    if (this.calibrationValues.invalid && hasCopyCandidates) {
      this.copyAsFoundToCalibrated(() => this.saveCalibration(confirmOutOfSpecification), true);
      return;
    }
    if (this.calibrationForm.invalid || this.calibrationValues.invalid) {
      this.calibrationForm.markAllAsTouched();
      this.calibrationValues.markAllAsTouched();
      return;
    }

    const form = this.calibrationForm.getRawValue();
    const request: CreateCalibrationRecordRequest = {
      instrumentCalibrationItemId: this.selectedAssignment.id,
      frequency: this.selectedAssignment.frequency,
      recordType: form.recordType,
      periodYear: form.periodYear,
      periodNumber: this.selectedAssignment.frequency === 'Annual' ? 1 : form.periodNumber,
      calibrationDate: form.calibrationDate,
      notes: form.notes.trim() || null,
      confirmOutOfSpecification,
      values: this.calibrationFields
        .filter(field => this.hasValue(field))
        .map(field => this.toCalibrationValue(field)),
    };

    this.isSavingCalibration = true;
    this.service.createCalibrationRecord(this.instrument.id, request)
      .pipe(finalize(() => this.isSavingCalibration = false))
      .subscribe({
        next: () => {
          this.selectAssignment(this.selectedAssignmentId!);
          this.loadAssignments();
          this.loadCalibrationHistory();
        },
        error: error => {
          if (error?.status === 422) {
            const response = error.error as OutOfSpecificationResponse;
            this.dialog.open(ConfirmDialogComponent, {
              width: '520px',
              maxWidth: 'calc(100vw - 32px)',
              data: {
                title: 'Values Outside Range',
                message: `${response.message} Review the readings below before saving this calibration.`,
                confirmLabel: 'Save Anyway',
                cancelLabel: 'Review Values',
                variant: 'warning',
                details: response.warnings.map(warning => ({
                  label: warning.label,
                  value: this.warningComparison(warning),
                })),
              },
            }).afterClosed().subscribe(confirmed => {
              if (confirmed) this.saveCalibration(true);
            });
          } else if (error?.status === 409) {
            this.dialog.open(ConfirmDialogComponent, {
              width: '460px',
              maxWidth: 'calc(100vw - 32px)',
              data: {
                title: 'Calibration Already Exists',
                message: `This instrument already has a calibration record for ${this.calibrationPeriod(request)}.`,
                confirmLabel: 'OK',
                cancelLabel: '',
                variant: 'warning',
              },
            });
          } else {
            this.showError(error, 'The calibration record could not be saved.');
          }
        },
      });
  }

  editCalibration(record: CalibrationRecord): void {
    if (this.isReadOnly) return;
    this.dialog.open(CalibrationRecordEditDialogComponent, {
      width: '760px',
      maxWidth: 'calc(100vw - 32px)',
      data: { record },
    }).afterClosed().subscribe(saved => {
      if (saved) {
        this.loadCalibrationHistory();
        this.loadAssignments();
      }
    });
  }

  calibrationStatusCount(record: CalibrationRecord, status: 'target' | 'range' | 'outside'): number {
    return record.values.filter(value => {
      if (status === 'outside') return value.isOutOfSpecification === true;
      if (value.isOutOfSpecification !== false || value.numericValue === null) return false;
      const isTarget = value.targetValue !== null && value.numericValue === value.targetValue;
      return status === 'target' ? isTarget : !isTarget;
    }).length;
  }

  private calibrationPeriod(request: CreateCalibrationRecordRequest): string {
    if (request.frequency === 'Annual') return `${request.periodYear}`;
    const period = `${request.frequency === 'Weekly' ? 'week' : request.frequency === 'Monthly' ? 'month' : 'quarter'} ${request.periodNumber}`;
    return `${period} of ${request.periodYear}`;
  }

  private warningComparison(warning: OutOfSpecificationResponse['warnings'][number]): string {
    const unit = warning.unit ? ` ${warning.unit}` : '';
    let expected = 'specified range';
    if (warning.minimumValue !== null && warning.maximumValue !== null) {
      expected = `${warning.minimumValue}-${warning.maximumValue}${unit}`;
    } else if (warning.minimumValue !== null) {
      expected = `at least ${warning.minimumValue}${unit}`;
    } else if (warning.maximumValue !== null) {
      expected = `at most ${warning.maximumValue}${unit}`;
    }
    return `Measured ${warning.value}${unit} · Expected ${expected}`;
  }

  loadCalibrationHistory(): void {
    if (!this.instrument) return;
    this.service.getCalibrationRecords(
      this.instrument.id,
      this.historyYear || undefined,
      this.historyMonth || undefined,
      this.historyQuarter || undefined,
      this.historyWeek || undefined,
    ).subscribe({
      next: result => this.calibrationRecords = result.items,
      error: error => this.showError(error, 'Calibration history could not be loaded.'),
    });
  }

  changeHistoryPeriod(kind: 'week' | 'month' | 'quarter'): void {
    if (kind !== 'week') this.historyWeek = null;
    if (kind !== 'month') this.historyMonth = null;
    if (kind !== 'quarter') this.historyQuarter = null;
    this.loadCalibrationHistory();
  }

  addAddendum(record: CalibrationRecord): void {
    const text = (this.addendumText[record.id] ?? '').trim();
    if (!text) return;
    this.service.addCalibrationAddendum(record.id, text).subscribe({
      next: () => {
        this.addendumText[record.id] = '';
        this.loadCalibrationHistory();
      },
      error: error => this.showError(error, 'The addendum could not be saved.'),
    });
  }

  saveMaintenance(): void {
    if (!this.instrument || this.isReadOnly || this.maintenanceForm.invalid) {
      this.maintenanceForm.markAllAsTouched();
      return;
    }
    const value = this.maintenanceForm.getRawValue();
    this.isSavingMaintenance = true;
    this.service.createMaintenanceRecord(this.instrument.id, value.type, value.performedDate, value.notes.trim())
      .pipe(finalize(() => this.isSavingMaintenance = false))
      .subscribe({
        next: () => {
          this.maintenanceForm.controls.notes.setValue('');
          this.loadMaintenance();
        },
        error: error => this.showError(error, 'The maintenance record could not be saved.'),
      });
  }

  fileSelected(event: Event): void {
    this.selectedFile = (event.target as HTMLInputElement).files?.[0] ?? null;
  }

  onFileDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onFileDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    this.selectedFile = event.dataTransfer?.files?.[0] ?? null;
  }

  uploadAttachment(): void {
    if (!this.instrument || !this.selectedFile || this.isReadOnly) return;
    this.isUploading = true;
    this.uploadProgress = 0;
    this.service.uploadAttachment(
      this.instrument.id,
      this.selectedFile,
      this.uploadMaintenanceRecordId ?? undefined,
      this.documentDate || undefined,
    ).pipe(finalize(() => {
      this.isUploading = false;
      this.uploadProgress = null;
    })).subscribe({
      next: event => {
        if (event.type === HttpEventType.UploadProgress && event.total) {
          this.uploadProgress = Math.round((event.loaded / event.total) * 100);
        }
        if (event.type === HttpEventType.Response) {
          this.selectedFile = null;
          this.uploadMaintenanceRecordId = null;
          this.documentDate = '';
          this.loadAttachments();
          this.loadMaintenance();
        }
      },
      error: error => this.showError(error, 'The attachment could not be uploaded.'),
    });
  }

  deleteAttachment(attachment: LabEquipmentAttachment): void {
    if (this.isReadOnly) return;
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Delete Attachment',
        message: `Permanently delete ${attachment.originalFileName}? This action cannot be undone.`,
        confirmLabel: 'Delete',
        cancelLabel: 'Cancel',
      },
    }).afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.service.deleteAttachment(attachment.id).subscribe({
        next: () => {
          this.loadAttachments();
          this.loadMaintenance();
        },
        error: error => this.showError(error, 'The attachment could not be deleted.'),
      });
    });
  }

  formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  attachmentMaintenanceRecord(attachment: LabEquipmentAttachment): MaintenanceRecord | undefined {
    return attachment.maintenanceRecordId === null
      ? undefined
      : this.maintenanceRecords.find(record => record.id === attachment.maintenanceRecordId);
  }

  fieldHint(field: CalibrationTemplateField): string {
    const parts: string[] = [];
    if (field.targetValue !== null) parts.push(`Target ${field.targetValue}${field.unit ? ' ' + field.unit : ''}`);
    if (field.minimumValue !== null || field.maximumValue !== null) {
      parts.push(`Range ${field.minimumValue ?? '—'}–${field.maximumValue ?? '—'}${field.unit ? ' ' + field.unit : ''}`);
    }
    if (field.acceptanceCriteria) parts.push(field.acceptanceCriteria);
    return parts.join(' · ');
  }

  fieldControl(field: CalibrationTemplateField): FormControl<string | number | boolean | null> {
    return this.calibrationValues.controls[this.fieldKey(field.id)];
  }

  numericValueStatus(field: CalibrationTemplateField): 'target' | 'range' | 'outside' | null {
    if (field.valueType !== 'Numeric') return null;

    const currentValue = this.fieldControl(field).value;
    if (currentValue === null || currentValue === '') return null;

    const numericValue = Number(currentValue);
    if (!Number.isFinite(numericValue)) return null;
    if (field.targetValue !== null && numericValue === field.targetValue) return 'target';

    const outsideMinimum = field.minimumValue !== null && numericValue < field.minimumValue;
    const outsideMaximum = field.maximumValue !== null && numericValue > field.maximumValue;
    if (outsideMinimum || outsideMaximum) return 'outside';

    return field.minimumValue !== null || field.maximumValue !== null ? 'range' : null;
  }

  createBladeLogEntry(): void {
    if (!this.instrument || this.isReadOnly || this.bladeInstallationForm.invalid) return;
    const value = this.bladeInstallationForm.getRawValue();
    if (value.weightBefore === null) return;

    this.isSavingBladeEntry = true;
    this.service.createBlenderBladeLogEntry(this.instrument.id, {
      blenderNumber: value.blenderNumber.trim(),
      installedDate: value.installedDate,
      weightBefore: value.weightBefore,
    }).pipe(finalize(() => this.isSavingBladeEntry = false)).subscribe({
      next: () => {
        this.bladeInstallationForm.reset({ blenderNumber: '', installedDate: this.today(), weightBefore: null });
        this.loadBladeLog();
      },
      error: error => this.showError(error, 'Blade installation could not be saved.'),
    });
  }

  completeBladeLogEntry(entry: BlenderBladeLogEntry): void {
    if (!this.instrument || this.isReadOnly) return;
    const removedDate = this.completionRemovedDates[entry.id];
    const weightAfter = this.completionWeights[entry.id];
    if (!removedDate || weightAfter === null || weightAfter === undefined || weightAfter <= 0) {
      this.errorMessage = 'Enter a valid removed date and weight after.';
      return;
    }

    this.completingBladeEntryId = entry.id;
    this.service.completeBlenderBladeLogEntry(this.instrument.id, entry.id, { removedDate, weightAfter })
      .pipe(finalize(() => this.completingBladeEntryId = null))
      .subscribe({
        next: () => this.loadBladeLog(),
        error: error => this.showError(error, 'Blade removal could not be saved.'),
      });
  }

  back(): void { void this.router.navigate(['/lab-equipment']); }

  private loadAll(id: number): void {
    this.isLoading = true;
    forkJoin({
      instrument: this.service.getInstrument(id),
      locations: this.service.getLocations(),
      items: this.service.getCalibrationTemplateItems(),
      assignments: this.service.getInstrumentCalibrationItems(id),
      records: this.service.getCalibrationRecords(id, this.historyYear),
      maintenance: this.service.getMaintenanceRecords(id),
      attachments: this.service.getAttachments(id),
      lifecycleEvents: this.service.getInstrumentLifecycleEvents(id),
    }).pipe(finalize(() => this.isLoading = false)).subscribe({
      next: result => {
        this.instrument = result.instrument;
        this.locations = result.locations;
        this.templateItems = result.items;
        this.assignments = result.assignments;
        this.calibrationRecords = result.records.items;
        this.maintenanceRecords = result.maintenance.items;
        this.attachments = result.attachments.items;
        this.lifecycleEvents = result.lifecycleEvents;
        if (this.isBlenderBladeWeightLog) this.loadBladeLog();
      },
      error: error => this.showError(error, 'Instrument details could not be loaded.'),
    });
  }

  private loadBladeLog(): void {
    if (!this.instrument || !this.isBlenderBladeWeightLog) return;
    this.service.getBlenderBladeLog(this.instrument.id).subscribe({
      next: entries => {
        this.bladeLogEntries = entries;
        for (const entry of entries.filter(candidate => candidate.removedDate === null)) {
          this.completionRemovedDates[entry.id] ??= this.today();
          this.completionWeights[entry.id] ??= null;
        }
      },
      error: error => this.showError(error, 'Blade weight log could not be loaded.'),
    });
  }

  private loadAssignments(): void {
    if (!this.instrument) return;
    this.service.getInstrumentCalibrationItems(this.instrument.id).subscribe({
      next: items => this.assignments = items,
      error: error => this.showError(error, 'Calibration assignments could not be loaded.'),
    });
  }

  private loadMaintenance(): void {
    if (!this.instrument) return;
    this.service.getMaintenanceRecords(this.instrument.id).subscribe({
      next: result => this.maintenanceRecords = result.items,
      error: error => this.showError(error, 'Maintenance history could not be loaded.'),
    });
  }

  private loadAttachments(): void {
    if (!this.instrument) return;
    this.service.getAttachments(this.instrument.id).subscribe({
      next: result => this.attachments = result.items,
      error: error => this.showError(error, 'Attachments could not be loaded.'),
    });
  }

  private fieldKey(id: number): string { return `field_${id}`; }

  private hasValue(field: CalibrationTemplateField): boolean {
    const value = this.calibrationValues.controls[this.fieldKey(field.id)]?.value;
    return field.valueType === 'Boolean' ? value !== null : value !== null && value !== '';
  }

  private toCalibrationValue(field: CalibrationTemplateField): CalibrationValueRequest {
    const value = this.calibrationValues.controls[this.fieldKey(field.id)].value;
    if (field.valueType === 'Numeric') {
      return { calibrationTemplateFieldId: field.id, numericValue: Number(value) };
    }
    if (field.valueType === 'Boolean') {
      return { calibrationTemplateFieldId: field.id, booleanValue: value as boolean };
    }
    return { calibrationTemplateFieldId: field.id, textValue: String(value).trim() };
  }

  private showError(error: unknown, fallback: string): void {
    const apiError = error as { error?: { detail?: string; title?: string; errors?: Record<string, string[]> } };
    this.errorMessage = apiError.error?.detail
      ?? apiError.error?.title
      ?? Object.values(apiError.error?.errors ?? {}).flat().join(' ')
      ?? fallback;
    if (!this.errorMessage) this.errorMessage = fallback;
  }

  private today(): string { return new Date().toISOString().slice(0, 10); }
}
